import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from './ui/Icon';
import { WorldItem } from './ui/WorldItem';
import { AuthUser, World, isStaffUser } from '../types';
import { Theme } from '../hooks/useTheme';

export type RailView = 'home' | 'worlds' | 'plots' | 'admin';

interface Props {
  me: AuthUser;
  worlds: World[];
  loading: boolean;
  view: RailView;
  selectedId: string | null;
  onHome: () => void;
  onPlots: () => void;
  onAdmin: () => void;
  onSelect: (id: string) => void;
  onLogout: () => void;
  onFeedback: () => void;
  theme: Theme;
  onToggleTheme: () => void;
}

/** Up to this width the rail is a drawer behind the top bar's menu button (same breakpoint as the responsive shell CSS). */
const DRAWER_QUERY = '(max-width: 820px)';

const Brand: React.FC = () => (
  <>
    <img className="mark mark--img" src="/bakerylogo.png" alt="" width={34} height={34} />
    <div>
      <b>Bakery Worlds Hub</b>
      {/* <small>private worlds portal</small> */}
    </div>
  </>
);

const Rail: React.FC<Props> = ({ me, worlds, loading, view, selectedId, onHome, onPlots, onAdmin, onSelect, onLogout, onFeedback, theme, onToggleTheme }) => {
  const [q, setQ] = useState('');
  // Narrow screens only: whether the drawer is out. Wider screens show the rail as a column whatever this says.
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);
  const staff = isStaffUser(me);
  const owned = useMemo(() => worlds.filter((w) => !!me.minecraftUuid && w.ownerId === me.minecraftUuid), [worlds, me.minecraftUuid]);
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return owned;
    return owned.filter((w) => w.name.toLowerCase().includes(t) || w.id.toLowerCase().includes(t) || String(w.status).toLowerCase().includes(t));
  }, [owned, q]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Growing past the breakpoint turns the drawer back into the column; forget it was open so it isn't out when the window shrinks again.
  useEffect(() => {
    if (!open || !window.matchMedia) return;
    const mq = window.matchMedia(DRAWER_QUERY);
    const onChange = () => !mq.matches && setOpen(false);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [open]);

  // Keyboard focus follows the drawer: onto its close button when it opens, back to the menu button when it closes.
  useEffect(() => {
    if (open) closeRef.current?.focus();
    else if (wasOpen.current) menuRef.current?.focus();
    wasOpen.current = open;
  }, [open]);

  /** Picking a destination closes the drawer. */
  const pick = (go: () => void) => () => {
    setOpen(false);
    go();
  };

  const avatar = me.user.avatar ? `https://cdn.discordapp.com/avatars/${me.user.id}/${me.user.avatar}.png?size=64` : null;
  const roles = [me.isDeveloper && 'dev', me.isAdmin && 'admin', me.isStaff && 'staff', me.isVIP && 'vip'].filter(Boolean) as string[];

  return (
    <>
      <div className="topbar">
        <div className="rail-brand">
          <Brand />
        </div>
        <button ref={menuRef} type="button" className="ibtn ibtn--lg" aria-label="Open menu" aria-expanded={open} aria-controls="rail" onClick={() => setOpen(true)}>
          <Icon name="menu" size={20} />
        </button>
      </div>
      <div className="rail-backdrop" data-open={open} onClick={() => setOpen(false)} />
      <aside id="rail" className="rail" data-open={open}>
        <div className="rail-brand">
          <Brand />
          <button ref={closeRef} type="button" className="ibtn ibtn--lg rail-close" aria-label="Close menu" onClick={() => setOpen(false)}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <nav className="rail-nav">
          <button type="button" className="rnav" aria-current={view === 'home'} onClick={pick(onHome)}>
            <Icon name="home" size={15} /> Your worlds
          </button>
          <button type="button" className="rnav" aria-current={view === 'plots'} onClick={pick(onPlots)}>
            <Icon name="block" size={15} /> Your plots
          </button>
          {staff && (
            <button type="button" className="rnav" aria-current={view === 'admin'} onClick={pick(onAdmin)}>
              <Icon name="shield" size={15} /> Admin <span className="tag">STAFF</span>
            </button>
          )}
        </nav>
        <div className="rail-scroll">
          <div className="rail-label">
            My worlds <i>{owned.length}</i>
          </div>
          {owned.length > 4 && <input className="input rail-search" placeholder="Filter worlds…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Filter worlds" />}
          {loading && owned.length === 0 && (
            <div className="rail-note" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span className="spin" style={{ width: 12, height: 12 }} /> Loading…
            </div>
          )}
          {!loading && !me.minecraftUuid && <div className="rail-note">No Minecraft account is linked to your Discord. Link it with DiscordSRV in-game to see your worlds.</div>}
          {!loading && me.minecraftUuid && owned.length === 0 && (
            <div className="rail-note">
              You don't have any worlds yet. Create one in-game with <span className="kbd">/pw</span>, or ask in Discord.
            </div>
          )}
          {owned.length > 0 && shown.length === 0 && <div className="rail-note">No worlds match your filter.</div>}
          {shown.map((w) => (
            <WorldItem key={w.id} world={w} selected={view === 'worlds' && selectedId === w.id} onClick={pick(() => onSelect(w.id))} />
          ))}
        </div>
        <div className="rail-foot">
          {avatar ? <img className="avatar" src={avatar} alt="" /> : <span className="avatar">{me.user.username.slice(0, 1)}</span>}
          <div className="who">
            <b title={me.user.username}>{me.user.username}</b>
            <small>
              {roles.length ? roles.map((r) => <span key={r} className="role">{r}</span>) : 'member'}
            </small>
          </div>
          <div className="acts">
            {/*<button type="button" className="ibtn" title="Send feedback" onClick={onFeedback}>
              <Icon name="chat" size={15} />
            </button>*/}
            <button type="button" className="ibtn" title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'} onClick={onToggleTheme}>
              <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={15} />
            </button>
            <button type="button" className="ibtn" title="Log out" onClick={onLogout}>
              <Icon name="logout" size={15} />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Rail;
